import {
  DiagramToCodePlugin,
  exportToBlob,
  getTextFromElements,
  MIME_TYPES,
  TTDDialog,
  AIImageDialog,
  AITextDialog,
  AIReadDialog,
  type EducationPromptInput,
  type OnEducationSubmitRetValue,
} from "@excalidraw/excalidraw";
import { getDataURL } from "@excalidraw/excalidraw/data/blob";
import { safelyParseJSON } from "@excalidraw/common";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

// Use Gemini 2.0 Flash for text, Nano Banana (2.5 Flash Image) for images
const geminiTextModel = import.meta.env.VITE_GEMINI_MODEL || "gemini-2.0-flash";
const geminiImageModel = "gemini-2.5-flash-image"; // Nano Banana - fast image generation
const geminiTTSModel = "gemini-2.5-flash-preview-tts"; // TTS model for speech generation
const geminiApiKey = import.meta.env.VITE_GEMINI_API_KEY;

const GEMINI_BASE_URL = "https://generativelanguage.googleapis.com/v1beta";

const extractGeminiText = (json: any) => {
  const parts =
    json?.candidates?.[0]?.content?.parts ||
    json?.candidates?.[0]?.content?.parts?.[0];
  if (!parts) {
    return null;
  }
  if (Array.isArray(parts)) {
    const textPart = parts.find((part) => part.text);
    return textPart?.text ?? null;
  }
  return parts.text ?? null;
};

const extractGeminiImageDataURL = (json: any) => {
  // Check for predict API format (Imagen)
  if (json?.predictions?.[0]?.bytesBase64Encoded) {
    return `data:image/png;base64,${json.predictions[0].bytesBase64Encoded}`;
  }

  // Check for generateContent format
  const parts = json?.candidates?.[0]?.content?.parts;
  if (!parts || !Array.isArray(parts)) {
    return null;
  }
  const inline = parts.find((part: any) => part.inlineData?.data);
  if (!inline?.inlineData?.data) {
    return null;
  }
  const mimeType = inline.inlineData.mimeType || "image/png";
  return `data:${mimeType};base64,${inline.inlineData.data}`;
};

// Convert PCM16 (linear16) audio to WAV format
const pcmToWav = (pcmBase64: string, sampleRate: number = 24000): string => {
  // Decode base64 to binary
  const binaryString = atob(pcmBase64);
  const pcmData = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    pcmData[i] = binaryString.charCodeAt(i);
  }

  // WAV header parameters
  const numChannels = 1;
  const bitsPerSample = 16;
  const byteRate = (sampleRate * numChannels * bitsPerSample) / 8;
  const blockAlign = (numChannels * bitsPerSample) / 8;
  const dataSize = pcmData.length;
  const headerSize = 44;
  const fileSize = headerSize + dataSize - 8;

  // Create WAV buffer
  const wavBuffer = new ArrayBuffer(headerSize + dataSize);
  const view = new DataView(wavBuffer);

  // Write WAV header
  // "RIFF" chunk descriptor
  view.setUint8(0, 0x52); // R
  view.setUint8(1, 0x49); // I
  view.setUint8(2, 0x46); // F
  view.setUint8(3, 0x46); // F
  view.setUint32(4, fileSize, true); // File size
  view.setUint8(8, 0x57); // W
  view.setUint8(9, 0x41); // A
  view.setUint8(10, 0x56); // V
  view.setUint8(11, 0x45); // E

  // "fmt " sub-chunk
  view.setUint8(12, 0x66); // f
  view.setUint8(13, 0x6d); // m
  view.setUint8(14, 0x74); // t
  view.setUint8(15, 0x20); // (space)
  view.setUint32(16, 16, true); // Subchunk1Size (16 for PCM)
  view.setUint16(20, 1, true); // AudioFormat (1 for PCM)
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);

  // "data" sub-chunk
  view.setUint8(36, 0x64); // d
  view.setUint8(37, 0x61); // a
  view.setUint8(38, 0x74); // t
  view.setUint8(39, 0x61); // a
  view.setUint32(40, dataSize, true);

  // Write PCM data
  const wavData = new Uint8Array(wavBuffer);
  wavData.set(pcmData, headerSize);

  // Convert to base64
  let binary = "";
  for (let i = 0; i < wavData.length; i++) {
    binary += String.fromCharCode(wavData[i]);
  }
  return `data:audio/wav;base64,${btoa(binary)}`;
};

const extractGeminiAudioDataURL = (json: any) => {
  // Check for generateContent format with audio
  const parts = json?.candidates?.[0]?.content?.parts;
  if (!parts || !Array.isArray(parts)) {
    console.log("[TTS] No parts found in response:", json);
    return null;
  }
  const inline = parts.find((part: any) => part.inlineData?.data);
  if (!inline?.inlineData?.data) {
    console.log("[TTS] No inlineData found in parts:", parts);
    return null;
  }

  const mimeType = inline.inlineData.mimeType || "";
  const base64Data = inline.inlineData.data;

  console.log("[TTS] Audio mimeType:", mimeType, "data length:", base64Data.length);

  // If it's already a supported format, return as-is
  if (
    mimeType === "audio/mp3" ||
    mimeType === "audio/mpeg" ||
    mimeType === "audio/wav" ||
    mimeType === "audio/ogg"
  ) {
    return `data:${mimeType};base64,${base64Data}`;
  }

  // Gemini TTS returns audio/L16 or audio/pcm - convert to WAV
  if (
    mimeType.includes("L16") ||
    mimeType.includes("pcm") ||
    mimeType.includes("raw") ||
    mimeType === "" ||
    mimeType === "audio/L16;rate=24000"
  ) {
    // Extract sample rate from mimeType if available (e.g., "audio/L16;rate=24000")
    let sampleRate = 24000;
    const rateMatch = mimeType.match(/rate=(\d+)/);
    if (rateMatch) {
      sampleRate = parseInt(rateMatch[1], 10);
    }

    console.log("[TTS] Converting PCM to WAV with sample rate:", sampleRate);
    return pcmToWav(base64Data, sampleRate);
  }

  // Fallback - try to use the mimeType as-is
  console.log("[TTS] Unknown mimeType, trying as-is:", mimeType);
  return `data:${mimeType || "audio/wav"};base64,${base64Data}`;
};

const buildEducationPrompt = (input: EducationPromptInput) => {
  const guidance = input.guidance ? `\nGuidance: ${input.guidance}` : "";
  return `You are creating concise educational material for ${input.audience}.
Topic: ${input.topic}
Format: ${input.format}
Constraints: 120-180 words, practical, friendly, with clear bullet points when helpful.${guidance}`;
};

const requestGeminiText = async (prompt: string) => {
  if (!geminiApiKey) {
    throw new Error("Gemini API key is missing (VITE_GEMINI_API_KEY).");
  }

  const response = await fetch(
    `${GEMINI_BASE_URL}/models/${geminiTextModel}:generateContent?key=${geminiApiKey}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        contents: [{ role: "user", parts: [{ text: prompt }] }],
      }),
    },
  );

  const json = await response.json();
  if (!response.ok) {
    throw new Error(json?.error?.message || "Gemini text request failed");
  }
  return json;
};

const requestGeminiImage = async (prompt: string) => {
  if (!geminiApiKey) {
    throw new Error("Gemini API key is missing (VITE_GEMINI_API_KEY).");
  }

  const response = await fetch(
    `${GEMINI_BASE_URL}/models/${geminiImageModel}:generateContent?key=${geminiApiKey}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        contents: [{ role: "user", parts: [{ text: prompt }] }],
        // Request image generation explicitly
        generationConfig: {
          responseModalities: ["IMAGE"],
        },
      }),
    },
  );

  const json = await response.json();
  if (!response.ok) {
    throw new Error(json?.error?.message || "Gemini image generation failed");
  }
  return json;
};

const requestGeminiTTS = async (text: string) => {
  if (!geminiApiKey) {
    throw new Error("Gemini API key is missing (VITE_GEMINI_API_KEY).");
  }

  const response = await fetch(
    `${GEMINI_BASE_URL}/models/${geminiTTSModel}:generateContent?key=${geminiApiKey}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        contents: [{ role: "user", parts: [{ text }] }],
        generationConfig: {
          responseModalities: ["AUDIO"],
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: {
                voiceName: "Kore", // Female voice, good for educational content
              },
            },
          },
        },
      }),
    },
  );

  const json = await response.json();
  if (!response.ok) {
    throw new Error(json?.error?.message || "Gemini TTS generation failed");
  }
  return json;
};

export const AIComponents = ({
  excalidrawAPI,
}: {
  excalidrawAPI: ExcalidrawImperativeAPI;
}) => {
  return (
    <>
      <DiagramToCodePlugin
        generate={async ({ frame, children }) => {
          const appState = excalidrawAPI.getAppState();

          const blob = await exportToBlob({
            elements: children,
            appState: {
              ...appState,
              exportBackground: true,
              viewBackgroundColor: appState.viewBackgroundColor,
            },
            exportingFrame: frame,
            files: excalidrawAPI.getFiles(),
            mimeType: MIME_TYPES.jpg,
          });

          const dataURL = await getDataURL(blob);

          const textFromFrameChildren = getTextFromElements(children);

          const response = await fetch(
            `${
              import.meta.env.VITE_APP_AI_BACKEND
            }/v1/ai/diagram-to-code/generate`,
            {
              method: "POST",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                texts: textFromFrameChildren,
                image: dataURL,
                theme: appState.theme,
              }),
            },
          );

          if (!response.ok) {
            const text = await response.text();
            const errorJSON = safelyParseJSON(text);

            if (!errorJSON) {
              throw new Error(text);
            }

            if (errorJSON.statusCode === 429) {
              return {
                html: `<html>
                <body style="margin: 0; text-align: center">
                <div style="display: flex; align-items: center; justify-content: center; flex-direction: column; height: 100vh; padding: 0 60px">
                  <div style="color:red">Too many requests today,</br>please try again tomorrow!</div>
                  </br>
                  </br>
                  <div>Please try again tomorrow.</div>
                </div>
                </body>
                </html>`,
              };
            }

            throw new Error(errorJSON.message || text);
          }

          try {
            const { html } = await response.json();

            if (!html) {
              throw new Error("Generation failed (invalid response)");
            }
            return {
              html,
            };
          } catch (error: any) {
            throw new Error("Generation failed (invalid response)");
          }
        }}
      />

      <TTDDialog
        onTextSubmit={async (input) => {
          try {
            const response = await fetch(
              `${
                import.meta.env.VITE_APP_AI_BACKEND
              }/v1/ai/text-to-diagram/generate`,
              {
                method: "POST",
                headers: {
                  Accept: "application/json",
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ prompt: input }),
              },
            );

            const rateLimit = response.headers.has("X-Ratelimit-Limit")
              ? parseInt(response.headers.get("X-Ratelimit-Limit") || "0", 10)
              : undefined;

            const rateLimitRemaining = response.headers.has(
              "X-Ratelimit-Remaining",
            )
              ? parseInt(
                  response.headers.get("X-Ratelimit-Remaining") || "0",
                  10,
                )
              : undefined;

            const json = await response.json();

            if (!response.ok) {
              if (response.status === 429) {
                return {
                  rateLimit,
                  rateLimitRemaining,
                  error: new Error(
                    "Too many requests today, please try again tomorrow!",
                  ),
                };
              }

              throw new Error(json.message || "Generation failed...");
            }

            const generatedResponse = json.generatedResponse;
            if (!generatedResponse) {
              throw new Error("Generation failed...");
            }

            return { generatedResponse, rateLimit, rateLimitRemaining };
          } catch (err: any) {
            throw new Error("Request failed");
          }
        }}
        onEducationSubmit={async (
          input: EducationPromptInput,
        ): Promise<OnEducationSubmitRetValue> => {
          try {
            const prompt = buildEducationPrompt(input);
            const textJson = await requestGeminiText(prompt);
            const text = extractGeminiText(textJson);

            let imageDataURL: string | null = null;
            if (input.includeImage) {
              try {
                const imagePrompt = `Create a simple, clean educational illustration for: ${input.topic}. Target audience: ${input.audience}. Style: minimal, clear, suitable for classroom use.`;
                const imageJson = await requestGeminiImage(imagePrompt);
                imageDataURL = extractGeminiImageDataURL(imageJson);
              } catch (imgErr: any) {
                console.warn("Image generation failed:", imgErr?.message);
                // Continue without image if generation fails
              }
            }

            if (!text && !imageDataURL) {
              return {
                error: new Error("Gemini returned an empty response"),
              };
            }

            return { text: text || undefined, imageDataURL };
          } catch (err: any) {
            return {
              error: new Error(err?.message || "Gemini request failed"),
            };
          }
        }}
      />

      {/* New AI dialogs for simple image/text generation */}
      <AIImageDialog
        onSubmit={async (prompt: string) => {
          try {
            const imagePrompt = `Create a simple, clean educational illustration for: ${prompt}. Style: minimal, clear, suitable for classroom use.`;
            const imageJson = await requestGeminiImage(imagePrompt);
            const imageDataURL = extractGeminiImageDataURL(imageJson);

            if (!imageDataURL) {
              return { error: new Error("Nie udało się wygenerować obrazka") };
            }

            return { imageDataURL };
          } catch (err: any) {
            return { error: new Error(err?.message || "Błąd generowania obrazka") };
          }
        }}
      />

      <AITextDialog
        onSubmit={async (prompt: string) => {
          try {
            const textPrompt = `Stwórz edukacyjne wyjaśnienie na temat: ${prompt}. Odpowiedź powinna być zwięzła, praktyczna i przyjazna. Używaj punktów gdy to pomocne. Maksymalnie 200 słów.`;
            const textJson = await requestGeminiText(textPrompt);
            const text = extractGeminiText(textJson);

            if (!text) {
              return { error: new Error("Nie udało się wygenerować tekstu") };
            }

            return { text };
          } catch (err: any) {
            return { error: new Error(err?.message || "Błąd generowania tekstu") };
          }
        }}
      />

      <AIReadDialog
        onSubmit={async (text: string) => {
          try {
            const ttsJson = await requestGeminiTTS(text);
            const audioDataURL = extractGeminiAudioDataURL(ttsJson);

            if (!audioDataURL) {
              return { error: new Error("Nie udało się wygenerować mowy") };
            }

            return { audioDataURL };
          } catch (err: any) {
            return { error: new Error(err?.message || "Błąd generowania mowy") };
          }
        }}
      />
    </>
  );
};
